/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
/* eslint-disable no-nested-ternary */
import styled from '@emotion/styled';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { FC, useState } from 'react';
import { AnyArgVoidReturnFunction } from 'types/utils';
import Image from '@base/Image';
import styleHelper from '@utils/styleHelper';
import AquillaButton from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography/Typography';
import BlueTick from 'aquilla/icons/stats/TestsAttempted';
import PlusMobile from 'aquilla/icons/subscriptions/PlusMobile';

const LiteSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UpperDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 ${Spacings.SPACING_6B} ${Spacings.SPACING_6B};
`;

const HeaderSectionWrapper = styled.div<{
  $hasExternalApps?: boolean;
}>`
  min-height: ${({ $hasExternalApps }) =>
    $hasExternalApps ? '130px' : '120px'};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    height: auto;
  }
`;

const ContentWrapperLiteWrapper = styled.div`
  & > :last-child {
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const ContentWrapperLite = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const ContentImageWrapperLite = styled.div`
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
`;

const ContentWrapper = styled.div<{
  $addTopMargin?: boolean;
}>`
  display: flex;
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
    height: auto;
  }
  height: auto;
  padding-right: ${Spacings.SPACING_6B};
`;

const FeatureDiv = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  flex-shrink: 0;
`;

const MobileMessage = styled.div`
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
    display: flex;
  }
`;

const LiteHeaderWrapper = styled.div`
  max-width: 30%;
  p {
    ${mediaQuery.uptoTablet} {
      max-width: 100%;
    }
  }
`;

const LeftHeaderWrapper = styled.div`
  width: 75%;
  @media only screen and (max-width: 350px) {
    margin-right: ${Spacings.SPACING_1B};
  }
  padding-top: ${Spacings.SPACING_6B};
`;

const RightHeaderWrapper = styled(LeftHeaderWrapper)``;

const SubCaption = styled(Typography)`
  color: var(--color-text-secondary);
  font-weight: normal;
  flex-wrap: wrap;
`;

const FeatureImageWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const IconButton = styled(AquillaButton)<{ $isSecondary: boolean }>`
  border: 1px solid var(--color-divider);
  font-size: ${Fonts.H5};
  color: ${({ $isSecondary }) =>
    $isSecondary ? 'var(--color-i-green);' : 'var(--color-text-tertiary)'};
  width: 100%;
  ${mediaQuery.uptoMobile} {
    padding: 1px 6px;
    height: ${Spacings.SPACING_12B};
  }
`;

const ListText = styled(SubCaption)`
  color: var(--color-i-black);
  margin-left: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-left: ${Spacings.SPACING_3B};
  }
`;

const SubHeading = styled(Typography)<{
  $iconic?: boolean;
  lineClamp: number;
}>`
  ${({ lineClamp }) =>
    lineClamp ? styleHelper.lineClampStyle(lineClamp) : null}
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_6B};
    width: 100%;
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_8B};
    max-width: 100%;
  }
`;

const CornerImage = styled(Image)`
  width: 100px !important;
  height: 100px !important;
`;

const ButtonContainer2 = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding: 0 ${Spacings.SPACING_6B} ${Spacings.SPACING_6B};

  & > :nth-child(even) {
    margin-top: ${Spacings.SPACING_4B};
  }

  ${mediaQuery.uptoMobile} {
    position: static !important;
    justify-content: initial;
    margin: 0px;
    flex-direction: row;
    & > :nth-child(even) {
      margin-top: 0 !important;
      width: 100%;
    }
    & > :nth-child(odd) {
      margin-top: 0 !important;
      margin-right: ${Spacings.SPACING_4B} !important;
      width: 100%;
    }
  }
`;

const PLusButton = styled(AquillaButton)`
  font-size: ${Fonts.H5};
  ${mediaQuery.uptoMobile} {
    width: 140px;
  }
`;

const blueTick = 'blueTick.png';
const greenTick = 'greenTick.png';
const yellowTick = 'yellowTick.png';
const liteIcon = 'liteIcon.png';

interface Props {
  subscriptionFeatures: any;
  features: {
    id: number;
    name: string;
    isPlus?: boolean;
  }[];
  selectBtnHandler: AnyArgVoidReturnFunction;
  // eslint-disable-next-line react/no-unused-prop-types
  hasExternalApps: boolean;
}

export const LiteFeatures: FC<Props> = ({
  subscriptionFeatures,
  features,
  hasExternalApps,
  selectBtnHandler
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const clickHandler = () => {
    setIsLoading(true);
    selectBtnHandler('Lite');
  };

  return (
    <>
      <UpperDiv>
        <HeaderSectionWrapper $hasExternalApps={hasExternalApps}>
          <LeftHeaderWrapper>
            <Typography variant="h2">LITE</Typography>
            <SubHeading lineClamp={2} variant="p2">
              {subscriptionFeatures?.liteDesc ||
                'Assess your preparation with tests'}
            </SubHeading>
          </LeftHeaderWrapper>
          <CornerImage
            width={subscriptionFeatures?.liteLogo?.width || '100'}
            height={subscriptionFeatures?.liteLogo?.height || '100'}
            src={subscriptionFeatures?.liteLogo?.url || '100'}
            alt="-"
          />
        </HeaderSectionWrapper>
        <ContentWrapper $addTopMargin={hasExternalApps}>
          <div>
            {features &&
              features.map((feature) => (
                <FeatureDiv key={feature.id}>
                  <FeatureImageWrapper>
                    <BlueTick width={24} height={24} />
                  </FeatureImageWrapper>

                  <ListText variant="h5">{feature.name}</ListText>
                </FeatureDiv>
              ))}
          </div>
        </ContentWrapper>
      </UpperDiv>
      <ButtonContainer2>
        <PLusButton
          label="Select LITE"
          onClick={clickHandler}
          variant="brand"
          fullWidth
          size="large"
          isLoading={isLoading}
        />
      </ButtonContainer2>
    </>
  );
};

export const LiteFlatFeatures: FC<Props> = ({
  subscriptionFeatures,
  features,
  selectBtnHandler
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const clickHandler = () => {
    setIsLoading(true);
    selectBtnHandler('Lite');
  };

  return (
    <>
      <LiteSectionWrapper>
        <LiteHeaderWrapper>
          <Typography variant="h2">LITE</Typography>
          <SubHeading lineClamp={2} variant="p2">
            {subscriptionFeatures?.liteDesc ||
              'Assess your preparation with tests'}
          </SubHeading>
        </LiteHeaderWrapper>
        <CornerImage
          $plus
          width={subscriptionFeatures?.liteLogo?.width}
          height={subscriptionFeatures?.liteLogo?.height}
          src={subscriptionFeatures?.liteLogo?.url || liteIcon}
          alt="-"
        />
        <ContentWrapperLiteWrapper>
          {features &&
            features.map((feature) => (
              <ContentWrapperLite key={feature.id}>
                <ContentImageWrapperLite>
                  <BlueTick width={24} height={24} />
                </ContentImageWrapperLite>
                <ListText variant="h5">{feature.name}</ListText>
              </ContentWrapperLite>
            ))}
        </ContentWrapperLiteWrapper>
        <PLusButton
          label="Select LITE"
          onClick={clickHandler}
          variant="brand"
          size="small"
          isLoading={isLoading}
        />
      </LiteSectionWrapper>
    </>
  );
};

export const PlusFeatures: FC<Props> = ({
  subscriptionFeatures,
  features,
  hasExternalApps,
  selectBtnHandler
}) => {
  const clickHandler = () => {
    selectBtnHandler('Plus');
  };

  return (
    <>
      <UpperDiv>
        <HeaderSectionWrapper $hasExternalApps={hasExternalApps}>
          <LeftHeaderWrapper>
            <Typography variant="h2">PLUS</Typography>
            <SubHeading lineClamp={2} variant="p2">
              {subscriptionFeatures?.plusDesc}
            </SubHeading>
          </LeftHeaderWrapper>
          <CornerImage
            $plus
            width={'100px' || subscriptionFeatures?.plusLogo?.width}
            height={'100px' || subscriptionFeatures?.plusLogo?.height}
            src={subscriptionFeatures?.plusLogo?.url}
            alt="-"
          />
        </HeaderSectionWrapper>
        <ContentWrapper $addTopMargin={hasExternalApps}>
          <div>
            {features &&
              features.map((feature) => {
                return (
                  <FeatureDiv key={feature.id}>
                    <FeatureImageWrapper>
                      <Image width={24} height={24} src={greenTick} alt="-" />
                    </FeatureImageWrapper>
                    <ListText variant="h5">{feature.name}</ListText>
                  </FeatureDiv>
                );
              })}
          </div>
        </ContentWrapper>
      </UpperDiv>
      <ButtonContainer2>
        <PLusButton
          label="Select PLUS"
          onClick={clickHandler}
          variant="brand"
          fullWidth
          size="large"
        />
      </ButtonContainer2>
    </>
  );
};

interface IconicFeaturesProps extends Props {
  openLearnMoreDrawer: AnyArgVoidReturnFunction;
}

export const IconicFeatures: FC<IconicFeaturesProps> = ({
  subscriptionFeatures,
  features,
  hasExternalApps,
  selectBtnHandler,
  openLearnMoreDrawer
}) => {
  const clickHandler = () => {
    selectBtnHandler('Iconic');
  };

  return (
    <>
      <UpperDiv>
        <HeaderSectionWrapper $hasExternalApps={hasExternalApps}>
          <RightHeaderWrapper>
            <Typography variant="h2">ICONIC </Typography>
            <SubHeading $iconic lineClamp={3} variant="p2">
              {subscriptionFeatures?.iconicDesc}
            </SubHeading>
          </RightHeaderWrapper>
          <CornerImage
            $hasExternalApps={hasExternalApps}
            width={'100px' || subscriptionFeatures?.iconicLogo.width}
            height={'100px' || subscriptionFeatures?.iconicLogo.height}
            src={subscriptionFeatures?.iconicLogo?.url}
            alt="-"
          />
        </HeaderSectionWrapper>
        <ContentWrapper>
          <div>
            {features && (
              <MobileMessage>
                <FeatureImageWrapper>
                  <PlusMobile height={24} width={24} />
                </FeatureImageWrapper>
                <ListText variant="h5">Everything included in PLUS</ListText>
              </MobileMessage>
            )}
            {features &&
              features.map((feature, index) => {
                return index > 3 ? (
                  <FeatureDiv key={feature.id}>
                    <FeatureImageWrapper>
                      <Image
                        width="24"
                        height="24"
                        src={
                          feature.isPlus
                            ? greenTick
                            : hasExternalApps
                            ? blueTick
                            : yellowTick
                        }
                        alt="-"
                      />
                    </FeatureImageWrapper>

                    <ListText variant="h5">{feature.name}</ListText>
                  </FeatureDiv>
                ) : null;
              })}
          </div>
        </ContentWrapper>
      </UpperDiv>
      <ButtonContainer2>
        <IconButton
          // @ts-ignore
          label="Learn more"
          onClick={openLearnMoreDrawer}
          variant="secondary"
          size="large"
          $isSecondary
        />
        <IconButton
          // @ts-ignore
          label="Select ICONIC"
          onClick={clickHandler}
          size="large"
          variant="brand"
          $isSecondary={false}
        />
      </ButtonContainer2>
    </>
  );
};
